
import { defineComponent, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'primevue/dialog';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import { showToastError, showToastSuccess } from '@/helpers';
import { setRefreshScenariosList } from '@/composables/AutomatedScenarios';
import { getBulkCampaignStatus } from '@/composables/workflows/CampaignBulk';
import { updateBulkStatus } from '@/composables/flows/useFlowApi';
import { CampaignsBulkStatusEnum } from '@/types';
import type { CampaignActionPayload, DeleteCampaignResult } from './types';

export default defineComponent({
  name: 'DeleteBulkCampaignAction',

  components: {
    Dialog,
    SpmButton,
  },

  emits: ['confirm'],

  setup(_, { emit, expose }) {
    const { t } = useI18n();

    // Internal state
    const isVisible = ref(false);
    const isLoading = ref(false);
    const currentPayload = ref<CampaignActionPayload | null>(null);

    /**
     * Open the dialog with the given payload
     */
    const open = (payload: CampaignActionPayload) => {
      currentPayload.value = payload;
      isVisible.value = true;
    };

    /**
     * Close the dialog
     */
    const close = () => {
      isVisible.value = false;
      currentPayload.value = null;
      isLoading.value = false;
    };

    /**
     * Confirm the action and emit result
     */
    const confirm = async () => {
      if (!currentPayload.value) return;

      isLoading.value = true;

      try {
        // Check if status hasn't changed on server
        const currentStatus = await getBulkCampaignStatus(currentPayload.value.campaignId);
        if (!currentStatus || currentStatus !== currentPayload.value.campaignStatus) {
          await showToastError(t('errorMessages.GENERIC_ERROR'));
          setRefreshScenariosList(true);
          close();
          return;
        }

        await updateBulkStatus(currentPayload.value.campaignId, CampaignsBulkStatusEnum.DELETED);
        await showToastSuccess(t('campaignsBulkList.actions.deleteSuccess'));
        setRefreshScenariosList(true);

        const result: DeleteCampaignResult = {
          campaignId: currentPayload.value.campaignId,
        };
        emit('confirm', result);
        close();
      } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : t('errorMessages.GENERIC_ERROR');
        await showToastError(errorMessage);
      } finally {
        isLoading.value = false;
      }
    };

    // Expose open method for parent component
    expose({ open });

    return {
      t,
      isVisible,
      isLoading,
      close,
      confirm,
    };
  },
});
